import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import Chart from "chart.js";
import { Checkbox, ListItemText, MenuItem, Select } from "@material-ui/core";

import useTranslation from "src/translations/useTranslation";
import { TravelAnalysisMessages } from "@shared/languages/interfaces";
import { TRAVEL_CYCLE_TARGET_RATE, TravelCycleOutcomes } from "@shared/constants/travel-cycle-causes.enum";
import { TravelCyclePlaceRow, TravelCycleSummary } from "@shared/interfaces/travel-cycle-analysis.interface";
import NewDataTable, { DataTableActions, DataTableColumns } from "@molecules/NewDataTable";
import { sortByText, sortByValue } from "@utils/data-table-sorting";

import { CYCLE_GROUP_OF, CYCLE_GROUPS, CYCLE_OUTCOMES, MIN_PLACE_TRAVELS, PLACE_CHART_COLORS } from "../../constants";
import { lineLabelPlugin } from "../../chartPlugins";
import { chartPlaces, dailyRates, dayLabel, formatCount, formatPercent, percentOf, rateColor } from "../../cycleUtils";
import { ChartContainer, CycleTable, PlacesToggles } from "../../styles";
import { DAILY_COLORS } from "./CycleDaily";

type PlaceKind = "regional" | "plant";
type PlaceView = "table" | "chart";

/** Color of the "overall" line, the whole filter. */
const OVERALL_COLOR = "#002951";

/** Props for the CyclePlaces organism. */
interface CyclePlacesProps {
  summary: TravelCycleSummary | null;
  /** The screen is filtered by regional or plant, so the chart starts with every filtered plant. */
  filteredByPlace: boolean;
  /** Click on a name: filter the screen by it. */
  onPlaceClick: (kind: PlaceKind, row: TravelCyclePlaceRow) => void;
}

/** Key of a breakdown row; the regional without a name has a null id. */
const keyOf = (row: TravelCyclePlaceRow): string => row.id ?? "none";

/**
 * Complete cycle by regional or plant (central), as a table (rate, failures by type with a popover, main
 * cause) or as a daily line chart of the chosen regionals or plants, with the overall line and the period
 * average. The name of a row filters the screen by that regional or plant.
 */
const CyclePlaces: React.FC<CyclePlacesProps> = ({ summary, filteredByPlace, onPlaceClick }) => {

  const { t, i18n } = useTranslation();
  const [kind, setKind] = useState<PlaceKind>("regional");
  const [view, setView] = useState<PlaceView>("table");
  const [selected, setSelected] = useState<string[]>([]);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart | null>(null);
  const rows = useMemo(() => (kind === "regional" ? summary?.regionals : summary?.plants) ?? [], [summary, kind]);
  // `t` is typed as possibly null across the platform
  const nameOf = (row: TravelCyclePlaceRow): string => row.name ?? t(TravelAnalysisMessages.noRegional) ?? "";
  const outcomeName = (outcome: TravelCycleOutcomes | null) => {
    const entry = CYCLE_OUTCOMES.find((e) => e.outcome === outcome);

    return entry ? t(entry.nameKey) ?? "" : "";
  };
  const average = summary ? percentOf(summary.complete, summary.total) : 0;

  // Lines of the chart start with the default choice each time the breakdown changes
  useEffect(() => {
    setSelected(chartPlaces(rows, kind, filteredByPlace).map(keyOf));
  }, [rows, kind, filteredByPlace]);

  /** Popover of the failures-by-type bar: one line per group, with share and travels. */
  const lossTip = (row: TravelCyclePlaceRow) => (
    <span className="tip">
      <span className="tip-title">
        {`${nameOf(row)} · ${formatPercent(percentOf(row.total - row.complete, row.total))} ${t(TravelAnalysisMessages.ofTravels)}`}
      </span>
      {CYCLE_GROUPS.map((g) => (
        <span key={g.key} className="tip-row">
          <i style={{ background: g.color }} />
          <span>{t(g.nameKey)}</span>
          <b>{formatPercent(percentOf(row.groups[g.key], row.total))}</b>
          <span>{`${formatCount(row.groups[g.key])} ${t(TravelAnalysisMessages.tripsUnit)}`}</span>
        </span>
      ))}
    </span>
  );

  const columns: DataTableColumns[] = [
    {
      title: kind === "regional" ? t(TravelAnalysisMessages.columnRegional) : t(TravelAnalysisMessages.columnPlant),
      data: sortByText<TravelCyclePlaceRow>(nameOf, (row) => ReactDOMServer.renderToString(
        <span>
          <button type="button" className="place-name">{nameOf(row)}</button>
          {kind === "plant" && <span className="cell-caption">{row.regionalName ?? t(TravelAnalysisMessages.noRegional)}</span>}
        </span>
      )),
      filterable: false
    },
    {
      title: t(TravelAnalysisMessages.columnTravels),
      data: sortByValue<TravelCyclePlaceRow>((row) => row.total, (row) => formatCount(row.total)),
      className: "dt-center",
      filterable: false
    },
    {
      title: t(TravelAnalysisMessages.columnCycleComplete),
      data: sortByValue<TravelCyclePlaceRow>((row) => percentOf(row.complete, row.total), (row) => {
        const rate = percentOf(row.complete, row.total);
        const color = rateColor(rate, row.total);

        return ReactDOMServer.renderToString(
          <span className="rate-cell">
            <b style={{ color }}>{formatPercent(rate)}</b>
            <span className="rate-bar"><i style={{ width: `${rate}%`, background: color }} /></span>
            {row.total < MIN_PLACE_TRAVELS && <span className="few-travels">{t(TravelAnalysisMessages.fewTravels)}</span>}
          </span>
        );
      }),
      filterable: false
    },
    {
      title: t(TravelAnalysisMessages.columnFailuresByType),
      orderable: false,
      data: (row: TravelCyclePlaceRow) => {
        const largestLoss = Math.max(1, ...rows.map((r) => percentOf(r.total - r.complete, r.total)));

        return ReactDOMServer.renderToString(
          <span className="tip-host">
            <span className="loss-bar">
              {CYCLE_GROUPS.map((g) => (
                <i key={g.key} style={{ width: `${percentOf(percentOf(row.groups[g.key], row.total), largestLoss)}%`, background: g.color }} />
              ))}
            </span>
            {lossTip(row)}
          </span>
        );
      },
      filterable: false
    },
    {
      title: t(TravelAnalysisMessages.columnTopCause),
      data: sortByText<TravelCyclePlaceRow>((row) => outcomeName(row.topOutcome), (row) => (row.topOutcome ? ReactDOMServer.renderToString(
        <span>
          <span className="cause-pill">
            <i style={{ background: CYCLE_GROUP_OF[row.topOutcome as keyof typeof CYCLE_GROUP_OF]?.color }} />
            {outcomeName(row.topOutcome)}
          </span>
          <span className="cell-caption">{`${formatPercent(percentOf(row.topOutcomeCount, row.total))} ${t(TravelAnalysisMessages.ofTravels)}`}</span>
        </span>
      ) : "—")),
      filterable: false
    }
  ];

  const actions: DataTableActions<TravelCyclePlaceRow>[] = [
    { ref: ".place-name", callback: (row: TravelCyclePlaceRow) => onPlaceClick(kind, row) }
  ];

  // Daily lines of the chosen regionals or plants, the overall line, the period average and the target
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    if (view !== "chart" || !canvasRef.current || !summary || !summary.days.length) return;

    const places = rows.filter((row) => selected.includes(keyOf(row)));
    const datasets: Chart.ChartDataSets[] = places.map((place) => {
      const color = PLACE_CHART_COLORS[rows.indexOf(place) % PLACE_CHART_COLORS.length];

      return {
        label: nameOf(place),
        data: dailyRates(place.daily),
        borderColor: color,
        backgroundColor: color,
        borderWidth: 2,
        pointRadius: 2,
        fill: false,
        lineTension: 0.25,
        spanGaps: true
      };
    });

    datasets.push({
      label: t(TravelAnalysisMessages.generalLine) ?? "",
      data: dailyRates(summary.daily),
      borderColor: OVERALL_COLOR,
      backgroundColor: OVERALL_COLOR,
      borderWidth: 2.5,
      borderDash: [6, 4],
      pointRadius: 0,
      fill: false,
      lineTension: 0.25,
      spanGaps: true
    }, {
      label: `${t(TravelAnalysisMessages.periodAverage)} (${formatPercent(average)})`,
      data: summary.days.map(() => Math.round(average * 10) / 10),
      borderColor: DAILY_COLORS.average,
      backgroundColor: DAILY_COLORS.average,
      borderWidth: 2.5,
      borderDash: [2, 3],
      pointRadius: 0,
      fill: false
    }, {
      label: `${t(TravelAnalysisMessages.target)} (${TRAVEL_CYCLE_TARGET_RATE}%)`,
      data: summary.days.map(() => TRAVEL_CYCLE_TARGET_RATE),
      borderColor: DAILY_COLORS.target,
      backgroundColor: DAILY_COLORS.target,
      borderWidth: 1,
      borderDash: [6, 4],
      pointRadius: 0,
      fill: false
    });

    const values = datasets.slice(0, -1).flatMap((d) => (d.data as (number | null)[]).filter((v) => v !== null)) as number[];
    const minRate = values.length ? Math.max(0, Math.floor((Math.min(...values) - 5) / 10) * 10) : 0;

    chartRef.current = new Chart(canvasRef.current, {
      type: "line",
      data: { labels: summary.days.map((day) => dayLabel(day, i18n.language)), datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 0 },
        legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 8 } },
        tooltips: {
          mode: "index",
          intersect: false,
          filter: (item) => (item.datasetIndex ?? 0) < datasets.length - 2,
          callbacks: {
            label: (item, data) => ` ${data.datasets?.[item.datasetIndex ?? 0]?.label ?? ""}: ${formatPercent(Number(item.yLabel))}`
          }
        },
        scales: {
          xAxes: [{ gridLines: { display: false }, ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 16 } }],
          yAxes: [{ ticks: { min: minRate, max: 100, stepSize: 10, callback: (value) => `${value}%` } }]
        }
      },
      plugins: [lineLabelPlugin(`${t(TravelAnalysisMessages.averageShort)} ${formatPercent(average)}`, average, DAILY_COLORS.average)]
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, kind, rows, summary, selected, i18n.language]);

  useEffect(() => () => {
    if (chartRef.current) chartRef.current.destroy();
  }, []);

  const toggle = (pressed: boolean, label: string | null, onClick: () => void) => (
    <button type="button" aria-pressed={pressed} onClick={onClick}>{label}</button>
  );

  return (
    <ChartContainer>
      <PlacesToggles>
        <div>
          <div className="chart-title">
            {kind === "regional" ? t(TravelAnalysisMessages.cyclePlacesByRegional) : t(TravelAnalysisMessages.cyclePlacesByPlant)}
          </div>
          <p className="chart-hint">{view === "table" ? t(TravelAnalysisMessages.cyclePlacesHint) : t(TravelAnalysisMessages.chartNotePlaces)}</p>
        </div>
        <div className="toggles">
          {view === "chart" && rows.length > 0 && (
            <Select
              multiple
              variant="outlined"
              margin="dense"
              value={selected}
              onChange={(event) => setSelected(event.target.value as string[])}
              renderValue={(value) => t(TravelAnalysisMessages.selectedPlaces, { count: (value as string[]).length })}
              style={{ minWidth: 190, height: 30, fontSize: "0.8rem", background: "#fff" }}
              MenuProps={{
                getContentAnchorEl: null,
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                PaperProps: { style: { maxHeight: 360 } }
              }}
            >
              {rows.map((row) => (
                <MenuItem key={keyOf(row)} value={keyOf(row)} dense>
                  <Checkbox size="small" color="primary" checked={selected.includes(keyOf(row))} />
                  <ListItemText
                    primary={nameOf(row)}
                    secondary={`${formatPercent(percentOf(row.complete, row.total))} · ${formatCount(row.total)}`}
                  />
                </MenuItem>
              ))}
            </Select>
          )}
          <div className="toggle-group" role="group">
            {toggle(kind === "regional", t(TravelAnalysisMessages.toggleRegional), () => setKind("regional"))}
            {toggle(kind === "plant", t(TravelAnalysisMessages.togglePlant), () => setKind("plant"))}
          </div>
          <div className="toggle-group" role="group">
            {toggle(view === "table", t(TravelAnalysisMessages.toggleTable), () => setView("table"))}
            {toggle(view === "chart", t(TravelAnalysisMessages.toggleChart), () => setView("chart"))}
          </div>
        </div>
      </PlacesToggles>
      {!rows.length ? (
        <div className="chart-empty">{t(TravelAnalysisMessages.noGroupedData)}</div>
      ) : view === "table" ? (
        <CycleTable>
          <NewDataTable
            key={kind}
            data={rows}
            columns={columns}
            actions={actions}
            settings={{ order: [[2, "asc"]] }}
            showPaging={rows.length > 10}
            showInfo={false}
            showSearching={false}
            scrollHeight=""
          />
        </CycleTable>
      ) : (
        <div className="places-chart"><canvas ref={canvasRef} /></div>
      )}
    </ChartContainer>
  );
};

export default CyclePlaces;
